// Crew certificates: is an upload the SAME certificate (same kind, same person) as one already on file? Only then is
// "replace" offered - a different kind is always a NEW record. One rule for the Crew page, the chat upload card and the AI.
// The kind comes from the title (else the Type field): "Type" alone is too generic ("Certificate", "STCW").

// order matters: the more specific names first. Each STCW Basic Training module is its OWN kind (PST, FPFF, EFA, PSSR - and
// PSC&RB, AFF, MFA, medical care, SSO, PSA, PDSD): "STCW Basic Training - Personal Survival Techniques" never replaces
// "... Fire Prevention and Fire Fighting". Only the combined course naming several modules (or none) is "basic_safety".
// STCW Code table A-VI numbers count as the module ("A-VI/1-2" = FPFF).
const KINDS = [
  ['passport', /\bpassport\b/],
  ['visa_schengen', /schengen/],
  ['visa_us', /\bb1\b|\bb2\b|\bc1 ?\/ ?d\b|\bus visa\b/],
  ['visa', /\bvisa\b/],
  ['seaman_book', /seam[ae]n'?s? ?book|discharge book|\bcdc\b|seafarer'?s? (identity|book)/],
  ['medical_first_aid', /medical first aid|\bmfa\b|a-?vi\/4-1\b/],
  ['medical_care', /medical care|a-?vi\/4-2\b/],
  ['medical', /\beng ?1\b|\bml ?5\b|medical (fitness|certificate|exam)|seafarer'?s? medical|fit for sea/],
  ['efa', /elementary first aid|\befa\b|a-?vi\/1-3\b/],
  ['first_aid', /first aid/],
  ['psc_rb', /survival craft|rescue boats?|\bpsc ?(&|and)? ?rb\b|\bpscrb\b|a-?vi\/2-1\b/],
  ['pst', /personal survival|\bpst\b|sea survival|a-?vi\/1-1\b/],
  ['aff', /advanced fire ?fighting|\baff\b|a-?vi\/3\b/],
  ['fpff', /fire prevention|\bfpff\b|fire ?fighting|a-?vi\/1-2\b/],
  ['pssr', /\bpssr\b|personal safety (and|&) social|a-?vi\/1-4\b/],
  ['psa', /security awareness|\bpsa\b|a-?vi\/6-1\b/],
  ['pdsd', /designated security|\bp?dsd\b|a-?vi\/6-2\b/],
  ['sso', /ship security officer|\bsso\b|a-?vi\/5\b/],
  ['basic_safety', /basic safety|basic training|\bbst\b|stcw (95|2010 )?basic/],
  ['gmdss_goc', /\bgoc\b|general operator/],
  ['gmdss_roc', /\broc\b|restricted operator/],
  ['gmdss', /gmdss/],
  ['radar', /\barpa\b|radar/],
  ['ecdis', /ecdis/],
  ['food_hygiene', /food (safety|hygiene)|food handl/],
  ['ships_cook', /ship'?s? cook/],
  ['yachtmaster_ocean', /yachtmaster ocean/],
  ['yachtmaster_offshore', /yachtmaster offshore/],
  ['yachtmaster_coastal', /yachtmaster coastal/],
  ['yachtmaster', /yachtmaster/],
  ['powerboat', /powerboat|\bpb ?2\b/],
  ['pwc', /personal water ?craft|\bpwc\b/],
  ['aec', /\baec\b|approved engine course/],
  ['meol', /\bmeol\b|marine engine operator/],
  ['hela', /\bhela\b/],
  ['crowd', /crowd management/],
  ['crisis', /crisis management/],
  ['coc', /\bcoc\b|certificate of competency|officer of the watch|\boow\b|\beoow\b|chief mate|\bmaster\b|chief engineer|second engineer|\by[1-6]\b/],
]
const BASIC_MODULES = ['pst', 'fpff', 'efa', 'pssr']
const OTHER_STCW = ['aff', 'psc_rb', 'medical_first_aid', 'medical_care', 'sso', 'pdsd', 'psa']
const BASIC_RE = KINDS.find(k => k[0] === 'basic_safety')[1]
const low = s => String(s || '').toLowerCase()
export const certKind = text => {
  const t = low(text)
  // the Basic Training course: one module named = that module; several (the combined course) or none = basic safety
  if (BASIC_RE.test(t)) {
    const other = OTHER_STCW.find(m => KINDS.find(k => k[0] === m)[1].test(t))   // "Basic Training - Advanced Fire Fighting" = AFF
    if (other) return other
    const mods = BASIC_MODULES.filter(m => KINDS.find(k => k[0] === m)[1].test(t)); return mods.length === 1 ? mods[0] : 'basic_safety'
  }
  const k = KINDS.find(([, re]) => re.test(t)); return k ? k[0] : null
}
export const kindOf = (title, type) => certKind(title) || certKind(type)

const words = s => low(s).normalize('NFD').replace(/[̀-ͯ]/g, '').split(/[^a-z0-9]+/).filter(Boolean)
// the certificate belongs to this person: first AND last name in its title ("Mia Torres - STCW Basic Safety")
export function isPersons(title, name) {
  const n = words(name), t = new Set(words(title))
  return n.length > 0 && t.has(n[0]) && t.has(n[n.length - 1])
}
// a title without the person and filler words, for certificates of no known kind
const FILL = new Set(['certificate', 'certification', 'cert', 'card', 'of', 'the', 'and', 'in', 'for', 'stcw'])
export const plainTitle = (title, name) => { const n = new Set(words(name)); return words(title).filter(w => !n.has(w) && !FILL.has(w) && !/^\d+$/.test(w)).join(' ') }

// docs: { id, title, type, expiry_date } of the vessel (or of the person); incoming: { title, type }.
// -> the certificate it would replace (same person + same kind; newest expiry when several), or null = a NEW record
export function findReplacement(docs, name, incoming) {
  if (!name || !incoming) return null
  const mine = (docs || []).filter(d => isPersons(d.title, name))
  const k = kindOf(incoming.title, incoming.type)
  const same = k ? mine.filter(d => kindOf(d.title, d.type) === k)
    : (() => { const p = plainTitle(incoming.title, name); return p ? mine.filter(d => !kindOf(d.title, d.type) && plainTitle(d.title, name) === p) : [] })()
  return same.sort((a, b) => String(b.expiry_date || '').localeCompare(String(a.expiry_date || '')))[0] || null
}

// what the card / form says
// the same words on every update card (crew certificate, ship document / service certificate, page or AI):
// "REPLACES: <title>, expiry <old> -> <new>" (just "<old>" when no new expiry is known or it is the same)
export const replaceLabel = (d, newExpiry) => {
  const old = (d && d.expiry_date) || 'none', nw = /^\d{4}-\d{2}-\d{2}/.test(String(newExpiry || '')) ? String(newExpiry).slice(0, 10) : ''
  return 'REPLACES: ' + ((d && d.title) || '') + ', expiry ' + old + (nw && nw !== old ? ' \u2192 ' + nw : '')
}
export const NEW_LABEL = 'NEW'
